import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { LANGUAGES } from '../utils/AppConstants';
import { useApi } from '../hooks/useApi';
import { saveSecureAuth, loadSecureAuth, clearSecureAuth } from "../utils/utilCrypto";
import PropTypes from "prop-types";

const AppContext = createContext();

export function AppProvider({ children }) {

    const [userData, setUserData] = useState({ id: '', name: '', email: '', mobile: '', profileImage: '' });
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authToken, setAuthToken] = useState(null);
    const [isNewChat, setIsNewChat] = useState(false);
    const [selectedLanguageData, setSelectedLanguageData] = useState({ code: 'en', label: 'English' });
    const [isChatFromRecentChat, setIsChatFromRecentChat] = useState(false);
    const [isChatStarted, setIsChatStarted] = useState(false);
    const [theme, setTheme] = useState("light");
    const [isRecording, setIsRecording] = useState(false);
    const [hasAudio, setHasAudio] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0].label);
    const sharedApiService = useApi();
    const [redirectTo, setRedirectTo] = useState("https://pgportal.gov.in/Signin");
    const [hasPayload, setHasPayload] = useState(false);

    const setInitialLanguage = () => setSelectedLanguageData({ code: 'en', label: 'English' });

    // Detect URL payload
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get("payload")) {
            setHasPayload(true);
        }
    }, []);

    // Ensure language data is always set
    useEffect(() => {
        if (!selectedLanguageData) setSelectedLanguageData({ code: 'en', label: 'English' });
    }, [selectedLanguageData]);

    const triggerRedirect = (pathOrUrl, delay = 1000) => {
        setTimeout(() => {
            if (pathOrUrl.startsWith("http")) window.location.href = pathOrUrl;
            else window.location.replace(pathOrUrl);
        }, delay);
    };

    const login = (user, token) => {
        setUserData(user);
        setAuthToken(token);
        setIsAuthenticated(true);

        const authData = { user, token, timestamp: Date.now() };
        if (!saveSecureAuth(authData)) console.error('Failed to save encrypted auth data');
    };

    const logout = async () => {
        try {
            setUserData({ id: "", name: "", email: "", mobile: "", profileImage: "" });
            clearSecureAuth();
            setAuthToken(null);
            setIsAuthenticated(false);
            setIsNewChat(false);
            setIsChatFromRecentChat(false);
            setIsChatStarted(false);

            sessionStorage.setItem("justLoggedOut", "true");

            localStorage.removeItem("cpgrams_user");
            localStorage.removeItem("cpgrams_token");
            localStorage.removeItem("cpgrams_auth_timestamp");
            sessionStorage.clear();
        } catch (error) {
            console.error("❌ Logout failed:", error);
        }
    };

    const updateUserData = (updates) => {
        const updatedUser = { ...userData, ...updates };
        setUserData(updatedUser);

        if (isAuthenticated) saveSecureAuth({ user: updatedUser, token: authToken, timestamp: Date.now() });
    };

    // Initialize authentication
    useEffect(() => {
        const initializeAuth = () => {
            try {
                if (sessionStorage.getItem("justLoggedOut")) {
                    sessionStorage.removeItem("justLoggedOut");
                    return;
                }

                const authData = loadSecureAuth();
                if (authData?.user && authData?.token && authData?.timestamp) {
                    const sessionAge = Date.now() - authData.timestamp;
                    const maxSessionAge = 24 * 60 * 60 * 1000;

                    if (sessionAge < maxSessionAge) {
                        setUserData(authData.user);
                        setAuthToken(authData.token);
                        setIsAuthenticated(true);
                    } else {
                        logout();
                    }
                } else {
                    console.log("ℹ️ No saved auth data found");
                }
            } catch (error) {
                console.error("❌ Error initializing auth:", error);
                logout();
            }
        };

        initializeAuth();
        localStorage.removeItem("transliterationCache");
    }, []);

    // Restore session from localStorage
    useEffect(() => {
        const savedUser = localStorage.getItem('cpgrams_user');
        const savedToken = localStorage.getItem('cpgrams_token');
        const authTimestamp = localStorage.getItem('cpgrams_auth_timestamp');

        if (savedUser && savedToken && authTimestamp) {
            const sessionAge = Date.now() - parseInt(authTimestamp, 10);
            const maxSessionAge = 24 * 60 * 60 * 1000;

            if (sessionAge < maxSessionAge) {
                try {
                    setUserData(JSON.parse(savedUser));
                    setAuthToken(savedToken);
                    setIsAuthenticated(true);
                } catch (error) {
                    console.error('Error parsing saved user data:', error);
                    logout();
                }
            } else logout();
        }
    }, []);
    const validateToken = async () => {
        return true;
    };

    const checkExistingAuth = async () => {
        try {
            const authData = loadSecureAuth();
            if (authData?.user && authData?.token) {
                const age = Date.now() - authData.timestamp;
                if (age < 24 * 60 * 60 * 1000) {
                    setUserData(authData.user);
                    setAuthToken(authData.token);
                    setIsAuthenticated(true);
                    return true;
                }
            }
            await logout();
            return false;
        } catch (err) {
            console.error("Auth check error:", err);
            await logout();
            return false;
        }
    };

    const contextValues = useMemo(
        () => ({
            userData,
            setUserData: updateUserData,
            isAuthenticated,
            authToken,
            login,
            logout,
            checkExistingAuth,
            triggerRedirect,
            redirectTo,
            setRedirectTo,
            validateToken,
            isChatFromRecentChat,
            setIsChatFromRecentChat,
            selectedLanguageData,
            setSelectedLanguageData,
            selectedLanguage,
            setSelectedLanguage,
            isNewChat,
            setIsNewChat,
            isChatStarted,
            setIsChatStarted,
            theme,
            setTheme,
            isRecording,
            setIsRecording,
            hasAudio,
            setHasAudio,
            setInitialLanguage,
            sharedApiService,
            hasPayload,
            setHasPayload,
        }),
        [
            userData,
            updateUserData,
            isAuthenticated,
            authToken,
            login,
            logout,
            checkExistingAuth,
            triggerRedirect,
            redirectTo,
            validateToken,
            isChatFromRecentChat,
            selectedLanguageData,
            selectedLanguage,
            isNewChat,
            isChatStarted,
            theme,
            isRecording,
            hasAudio,
            setInitialLanguage,
            sharedApiService,
            hasPayload,
        ]
    );

    return <AppContext.Provider value={contextValues}>{children}</AppContext.Provider>;
}

AppProvider.propTypes = {
    children: PropTypes.node.isRequired,
};

export const useAppContext = () => {
    const context = useContext(AppContext);
    if (!context) throw new Error('useAppContext must be used within an AppProvider');
    return context;
};
